import FunnelLayout from "@components/quiz/FunnelLayout"
import FunnelLogo from "@components/quiz/FunnelLogo"
import SubscriptionDialog from "@components/SubscriptionDialog"
import getAppStoreBadge from "@helpers/getAppStoreBadge"
import getGooglePlayBadge from "@helpers/getGooglePlayBadge"
import { SUPPORT_EMAIL } from "@helpers/helpCenterResult"
import localizeDate from "@helpers/localizeDate"
import { getSubscriptionState, hasAccess } from "@helpers/subscriptionState"
import { lightColors } from "@helpers/themeColor"
import { CheckIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline"
import useDeviceType, { DeviceType } from "@hooks/useDeviceType"
import { QRCodeSVG } from "qrcode.react"
import { ReactNode, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link, useLoaderData } from "react-router-dom"
import { GetSubscriptionLoaderData } from "src/loaders/getSubscriptionLoader"
import { RevenueCatSubscription } from "src/types"

const COPIED_FEEDBACK_MS = 2000
const IN_APP_BROWSER_PATTERN =
  /Instagram|FBAN|FBAV|FB_IAB|Line|MicroMessenger|wv/i

// NOTE: No fallback to an expired or refunded one: it would be shown as
// "Your subscription is active".
function findActiveSubscription(
  subscriptions: RevenueCatSubscription[],
): RevenueCatSubscription | undefined {
  const now = Date.now()

  return (
    subscriptions.find((s) => getSubscriptionState(s, now) === "active") ??
    subscriptions.find((s) => hasAccess(s, now))
  )
}

function SuccessBadge() {
  return (
    <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
      <span className="absolute inset-0 animate-ping rounded-full bg-vm-green/20 [animation-iteration-count:2]" />
      <span className="absolute inset-0 rounded-full bg-vm-green-soft" />
      <span className="relative flex h-14 w-14 animate-scale-in items-center justify-center rounded-full bg-vm-green-solid shadow-[0_8px_24px_rgb(var(--vm-green-solid)/0.35)]">
        <CheckIcon className="h-8 w-8 text-white" strokeWidth={3} />
      </span>
    </div>
  )
}

function SubscriptionStatusCard({
  subscription,
}: {
  subscription: RevenueCatSubscription
}) {
  const { t } = useTranslation()
  const willRenew = !subscription.unsubscribe_detected_at

  return (
    <div className="vm-card animate-fade-up p-5 text-center [animation-delay:200ms]">
      <p className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-vm-green-text">
        <span className="h-2 w-2 rounded-full bg-vm-green" />
        {t("pages.account.subscription.statusActive")}
      </p>

      <div className="flex justify-center gap-10 text-sm">
        <div>
          <p className="text-vm-muted">
            {t("pages.account.subscription.started")}
          </p>
          <p className="mt-0.5 font-semibold text-vm-ink">
            {localizeDate(new Date(subscription.purchase_date))}
          </p>
        </div>

        <div>
          <p className="text-vm-muted">
            {t(
              willRenew
                ? "pages.account.subscription.renewsOn"
                : "pages.account.subscription.endsOn",
            )}
          </p>
          <p className="mt-0.5 font-semibold text-vm-ink">
            {localizeDate(new Date(subscription.expires_date))}
          </p>
        </div>
      </div>
    </div>
  )
}

function StepCard({
  children,
  index,
  text,
  title,
}: {
  children?: ReactNode
  index: number
  text: ReactNode
  title: string
}) {
  return (
    <div
      className="vm-card animate-fade-up p-5"
      style={{ animationDelay: `${350 + index * 120}ms` }}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-vm-primary-solid text-sm font-bold text-white">
          {index + 1}
        </span>

        <div className="min-w-0 grow pt-0.5">
          <p className="text-[15px] font-semibold text-vm-ink">{title}</p>
          <div className="mt-1 text-sm leading-relaxed text-vm-muted">
            {text}
          </div>
        </div>
      </div>

      {children}
    </div>
  )
}

function StoreBadges({ device }: { device: DeviceType }) {
  const appStore = (
    <a
      className="transition-opacity hover:opacity-80"
      href={import.meta.env.VITE_APP_URL_APPLE_APP_STORE as string}
      key="app-store"
      rel="noreferrer"
      target="_blank"
    >
      <img
        alt="Download VisualMind on the App Store"
        className="h-10 w-auto"
        src={getAppStoreBadge()}
      />
    </a>
  )

  const googlePlay = (
    <a
      className="transition-opacity hover:opacity-80"
      href={import.meta.env.VITE_APP_URL_GOOGLE_PLAY_STORE as string}
      key="google-play"
      rel="noreferrer"
      target="_blank"
    >
      <img
        alt="Get VisualMind on Google Play"
        className="h-10 w-auto"
        src={getGooglePlayBadge()}
      />
    </a>
  )

  return (
    <div className="flex flex-wrap justify-center gap-3">
      {device === "android" ? [googlePlay, appStore] : [appStore, googlePlay]}
    </div>
  )
}

function CopyEmailButton({ email }: { email: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [emailLocalPart, emailDomain] = email.split("@")

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email)
      setCopied(true)
      setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
    } catch {
      // Clipboard access is denied in some in-app browsers; the email stays
      // visible and selectable, so there is nothing else to do.
    }
  }

  return (
    <button
      className={`mt-3 flex w-full items-center gap-3 rounded-vm-md border px-3 py-2.5 text-left transition-colors ${
        copied
          ? "border-vm-green/30 bg-vm-green-soft"
          : "border-vm-line bg-vm-cream hover:border-vm-primary/40"
      }`}
      onClick={() => void handleCopy()}
      type="button"
    >
      <span className="min-w-0 grow text-sm font-semibold text-vm-ink [overflow-wrap:anywhere]">
        {emailLocalPart}@<wbr />
        {emailDomain}
      </span>

      <span
        className={`flex shrink-0 items-center gap-1 text-xs font-semibold ${
          copied ? "text-vm-green-text" : "text-vm-primary-text"
        }`}
      >
        {copied ? (
          <CheckIcon className="h-4 w-4" strokeWidth={2.5} />
        ) : (
          <DocumentDuplicateIcon className="h-4 w-4" />
        )}
        {t(
          copied
            ? "pages.account.subscription.copied"
            : "pages.account.subscription.tapToCopy",
        )}
      </span>
    </button>
  )
}

export default function SubscriptionPage() {
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false)
  const { revenueCatSubscriptions, userEmail } =
    useLoaderData() as GetSubscriptionLoaderData

  const { t } = useTranslation()
  const device = useDeviceType()
  const isInAppBrowser = IN_APP_BROWSER_PATTERN.test(navigator.userAgent)

  const activeSubscription = findActiveSubscription(revenueCatSubscriptions)
  const isProUser = activeSubscription !== undefined
  const hasSubscriptionHistory = revenueCatSubscriptions.length > 0

  const header = (
    <header className="vm-container flex items-center justify-between py-4">
      <FunnelLogo />

      {hasSubscriptionHistory ? (
        <button
          className="text-sm font-semibold text-vm-muted underline underline-offset-4 hover:text-vm-ink"
          onClick={() => setOpenSubscriptionDialog(true)}
          type="button"
        >
          {t("pages.account.subscription.subscriptionInfo")}
        </button>
      ) : (
        <Link
          className="text-sm font-semibold text-vm-muted underline underline-offset-4 hover:text-vm-ink"
          to="/account/sign-in"
        >
          {t("shared.signIn")}
        </Link>
      )}
    </header>
  )

  return (
    <>
      <FunnelLayout header={header}>
        <div className="flex flex-col gap-6 pb-10 pt-4">
          <div className="space-y-3 text-center">
            {isProUser && <SuccessBadge />}

            <h1 className="vm-heading animate-fade-up">
              <Trans
                components={{ colored: <span className="vm-highlight" /> }}
                i18nKey="pages.account.subscription.heading"
              />
            </h1>

            <p className="vm-subheading animate-fade-up [animation-delay:100ms]">
              {t(
                isProUser
                  ? "pages.account.subscription.proSubheading"
                  : "pages.account.subscription.subheading",
              )}
            </p>
          </div>

          {activeSubscription && (
            <SubscriptionStatusCard subscription={activeSubscription} />
          )}

          <div className="space-y-3">
            <h2 className="animate-fade-up text-center text-lg font-bold text-vm-ink [animation-delay:300ms]">
              {t("pages.account.subscription.stepsHeading")}
            </h2>

            <StepCard
              index={0}
              text={t(
                device === "desktop"
                  ? "pages.account.subscription.steps.download.textDesktop"
                  : "pages.account.subscription.steps.download.text",
              )}
              title={t("pages.account.subscription.steps.download.title")}
            >
              <div className="mt-4 flex flex-col items-center gap-4">
                {device === "desktop" && (
                  // NOTE: White in dark mode too: dark on light scans best.
                  <div
                    className="rounded-card border border-vm-line p-3 shadow-card"
                    style={{ backgroundColor: lightColors.background }}
                  >
                    <QRCodeSVG
                      bgColor={lightColors.background}
                      fgColor={lightColors.text}
                      size={140}
                      value={`${window.location.origin}/account/download`}
                    />
                  </div>
                )}

                <StoreBadges device={device} />

                {isInAppBrowser && (
                  <p className="text-center text-xs text-vm-muted">
                    {t("pages.account.subscription.inAppBrowserHint")}
                  </p>
                )}
              </div>
            </StepCard>

            <StepCard
              index={1}
              text={
                <Trans
                  components={{
                    bold: <span className="font-semibold text-vm-ink" />,
                  }}
                  i18nKey="pages.account.subscription.steps.login.text"
                />
              }
              title={t("pages.account.subscription.steps.login.title")}
            >
              {userEmail && <CopyEmailButton email={userEmail} />}
            </StepCard>

            <StepCard
              index={2}
              text={t("pages.account.subscription.steps.explore.text")}
              title={t("pages.account.subscription.steps.explore.title")}
            />
          </div>

          {isProUser && (
            <p className="animate-fade-up rounded-card border border-vm-primary/20 bg-vm-primary-soft px-5 py-4 text-center text-sm font-medium text-vm-primary-text [animation-delay:750ms]">
              {t("pages.account.subscription.noNewSubscription")}
            </p>
          )}

          <div className="animate-fade-up text-center [animation-delay:850ms]">
            <p className="font-semibold text-vm-ink">
              {t("pages.account.subscription.supportHeading")}
            </p>
            <p className="mt-1 text-sm text-vm-muted">
              {t("pages.account.subscription.supportBody")}
            </p>
            <a
              className="mt-2 inline-block text-sm font-semibold text-vm-primary-text underline underline-offset-4"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </FunnelLayout>

      {openSubscriptionDialog && hasSubscriptionHistory && (
        <SubscriptionDialog
          onClose={() => setOpenSubscriptionDialog(false)}
          open={openSubscriptionDialog}
          subscriptions={revenueCatSubscriptions}
        />
      )}
    </>
  )
}
